import React, { useEffect, useRef, useState } from 'react'

import { Card, CardBody, Col, Label, Row } from 'reactstrap'
import Pie from './pie'

import { amountCommaSeparateFunc, date_ymd_func, } from '../../../components/Common/CommonFunction'

import { useParams } from 'react-router-dom';

import { convertTo12Hour, formatDate, GetDailySaleData } from './Function'
import { C_DatePicker } from '../../../CustomValidateForm';
import { Go_Button } from '../../../components/Common/CommonButton';

const toDMY = (d) => (d ? d.split("-").reverse().join("-") : "");

const cardStyle = {
    borderRadius: "14px",
    border: "none",
    backgroundColor: "#ffffff",
    boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
    marginBottom: "14px",
};

const DailyItemSaleView = () => {

    const [selectedOption, setSelectedOption] = useState('TODAYS');
    const { Party_Id } = useParams();

    const [data, setData] = useState({});
    const [loading, setloading] = useState(true);
    const requestRef = useRef(0);

    const [dateRange, setDateRange] = useState({
        fromDate: date_ymd_func(new Date()),
        toDate: date_ymd_func(new Date()),
    });

    // dates of the report currently shown (used in the empty-state pill)
    const [shownRange, setShownRange] = useState(dateRange);

    const [CoustomDate, setCoustomDate] = useState({
        fromDate: date_ymd_func(new Date()),
        toDate: date_ymd_func(new Date()),
    });

    const decodeId = (encodedId) => {
        return atob(encodedId); // Converts Base64 encoded string back to ID
    };

    // One fetch function for both the dropdown and the Go button.
    // Never throws, and never stores undefined, so the page cannot crash.
    const fetchReport = async (fromDate, toDate) => {
        const requestId = ++requestRef.current;
        setloading(true);
        setData({});
        setShownRange({ fromDate, toDate });

        try {
            const partyId = Number(decodeId(Party_Id));
            const jsonData = await GetDailySaleData({ fromDate, toDate, Party_Id: partyId });
            if (requestId !== requestRef.current) return; // stale response
            setData(jsonData?.Data?.[0] || {});
        } catch (error) {
            if (requestId !== requestRef.current) return;
            console.error("Error fetching daily sale report", error);
            setData({});
        } finally {
            if (requestId === requestRef.current) setloading(false);
        }
    };

    const handleChange = (event) => {
        const value = event.target.value;
        setSelectedOption(value);

        const today = new Date();
        let fromDate;
        let toDate;

        if (value === 'TODAYS') {
            fromDate = toDate = formatDate(today);
        } else if (value === 'YESTERDAY') {
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            fromDate = toDate = formatDate(yesterday);
        } else if (value === 'MONTH') {
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            fromDate = formatDate(firstDayOfMonth);
            toDate = formatDate(today);
        } else if (value === 'SIX_MONTH') {
            const sixMonthsAgo = new Date(today);
            sixMonthsAgo.setMonth(today.getMonth() - 6);
            fromDate = formatDate(sixMonthsAgo);
            toDate = formatDate(today);
        } else if (value === 'CUSTOM') {
            return
        }

        setDateRange({ fromDate, toDate });
    };

    // NOTE: effect callback must NOT be async
    useEffect(() => {
        fetchReport(dateRange.fromDate, dateRange.toDate);
        return () => { requestRef.current += 1; }; // ignore late responses after unmount
    }, [Party_Id, dateRange]);

    const goButtonHandler = () => {
        fetchReport(CoustomDate.fromDate, CoustomDate.toDate);
    };

    const hasData = Object.keys(data || {}).length > 0;
    const hasItems = data?.TopSaleItems?.length > 0;

    // ---------- reusable empty state ----------
    const renderEmptyState = (height = "230px") => (
        <div
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{ minHeight: height, padding: "20px 16px" }}
        >
            <div
                style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(78,115,223,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "14px",
                }}
            >
                <i className="bx bx-pie-chart-alt-2" style={{ fontSize: "30px", color: "#4e73df" }}></i>
            </div>

            <div style={{ fontSize: "15px", fontWeight: 600, color: "#343a40", marginBottom: "4px" }}>
                No data available
            </div>

            <div style={{ fontSize: "12px", color: "#6c757d", maxWidth: "260px", lineHeight: 1.5, marginBottom: "10px" }}>
                No items were sold in this period. Try a different date range.
            </div>

            <span
                style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#4e73df",
                    backgroundColor: "rgba(78,115,223,0.08)",
                    borderRadius: "999px",
                    padding: "4px 12px",
                }}
            >
                {shownRange.fromDate === shownRange.toDate
                    ? toDMY(shownRange.fromDate)
                    : `${toDMY(shownRange.fromDate)}  →  ${toDMY(shownRange.toDate)}`}
            </span>
        </div>
    );

    return (
        <div style={{ backgroundColor: "#f4f6fb", minHeight: "100vh", padding: "12px" }}>
            <div style={{ maxWidth: "960px", margin: "0 auto" }}>

                {/* ===== FILTER CARD (always visible) ===== */}
                <Card style={cardStyle}>
                    <CardBody style={{ padding: "14px 16px" }}>
                        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "10px" }}>
                            <div style={{ fontSize: "15px", fontWeight: 600, color: "#343a40" }}>
                                Daily Sales Report
                            </div>

                            <select
                                className="form-select form-select-sm"
                                style={{
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                    padding: "6px 30px 6px 12px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #dee2e6",
                                    minWidth: "150px",
                                    width: "auto",
                                }}
                                value={selectedOption}
                                onChange={handleChange}
                            >
                                <option value="TODAYS">Today's</option>
                                <option value="YESTERDAY">Yesterday</option>
                                <option value="MONTH">Month</option>
                                <option value="SIX_MONTH">Six Month</option>
                                <option value="CUSTOM">Custom Date</option>
                            </select>
                        </div>

                        {selectedOption === "CUSTOM" && (
                            <div
                                className="d-flex align-items-end flex-wrap"
                                style={{ marginTop: "12px", gap: "12px", borderTop: "1px solid #eef0f4", paddingTop: "12px" }}
                            >
                                <div>
                                    <Label style={{ fontSize: "12px", color: "#6c757d", fontWeight: 600, marginBottom: "4px" }}>
                                        From Date
                                    </Label>
                                    <C_DatePicker
                                        options={{
                                            altInput: true,
                                            altFormat: "d-m-Y",
                                            dateFormat: "Y-m-d",
                                            maxDate: "today",
                                        }}
                                        name="FromDate"
                                        value={CoustomDate.fromDate}
                                        onChange={(e, date) => {
                                            setCoustomDate((i) => ({ ...i, fromDate: date }))
                                        }}
                                    />
                                </div>

                                <div>
                                    <Label style={{ fontSize: "12px", color: "#6c757d", fontWeight: 600, marginBottom: "4px" }}>
                                        To Date
                                    </Label>
                                    <C_DatePicker
                                        options={{
                                            altInput: true,
                                            altFormat: "d-m-Y",
                                            dateFormat: "Y-m-d",
                                            maxDate: "today",
                                        }}
                                        name="ToDate"
                                        value={CoustomDate.toDate}
                                        onChange={(e, date) => {
                                            setCoustomDate((i) => ({ ...i, toDate: date }))
                                        }}
                                    />
                                </div>

                                <div>
                                    <Go_Button onClick={goButtonHandler} loading={loading} />
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* ===== LOADING ===== */}
                {loading ? (
                    <Card style={cardStyle}>
                        <CardBody
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{ minHeight: "300px" }}
                        >
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "12px" }}>
                                Fetching details...
                            </div>
                        </CardBody>
                    </Card>

                ) : !hasData ? (
                    /* ===== NO DATA AT ALL ===== */
                    <Card style={cardStyle}>
                        <CardBody>{renderEmptyState("300px")}</CardBody>
                    </Card>

                ) : (
                    <>
                        {/* ===== PARTY + TOTAL ===== */}
                        <Card style={cardStyle}>
                            <CardBody style={{ padding: "16px" }}>
                                <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "12px" }}>
                                    <div>
                                        <div style={{ fontSize: "18px", fontWeight: 600, color: "#343a40" }}>
                                            {data?.PartyName}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "2px" }}>
                                            {data?.PartyAddress}
                                        </div>
                                        <span
                                            className="badge rounded-pill badge-soft-primary"
                                            style={{ fontSize: "12px", marginTop: "8px", padding: "5px 12px" }}
                                        >
                                            Bill Count: {data?.BillCount ?? 0}
                                        </span>
                                    </div>

                                    <div className="text-end">
                                        <div style={{ fontSize: "11px", color: "#6c757d", fontWeight: 600, marginBottom: "4px" }}>
                                            TOTAL SALES
                                        </div>
                                        <span
                                            className="badge rounded-pill badge-soft-primary"
                                            style={{ fontSize: "24px", padding: "8px 18px" }}
                                        >
                                            ₹ {data?.TotalAmount ? amountCommaSeparateFunc(data.TotalAmount) : 0}
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* ===== TOP SELLING ITEMS ===== */}
                        {hasItems && (
                            <Card style={cardStyle}>
                                <CardBody style={{ padding: "14px 16px" }}>
                                    <div style={{ fontSize: "14px", fontWeight: 600, color: "#343a40", marginBottom: "8px" }}>
                                        Top Selling Items
                                    </div>

                                    {data.TopSaleItems.map((element, index) => (
                                        <div
                                            key={index}
                                            className="d-flex align-items-center"
                                            style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f7", gap: "8px" }}
                                        >
                                            <div className="flex-grow-1" style={{ fontSize: "14px", color: "#343a40" }}>
                                                {element.ItemName}
                                            </div>
                                            <span style={{ minWidth: "90px" }} className="badge rounded-pill badge-soft-success font-size-12 fw-medium">
                                                {element.TotalQuantity} {element.UnitName}
                                            </span>
                                            <span style={{ minWidth: "90px" }} className="badge rounded-pill badge-soft-primary font-size-12 fw-medium">
                                                ₹&nbsp;{amountCommaSeparateFunc(element.TotalAmount)}
                                            </span>
                                        </div>
                                    ))}

                                    {data?.UnitPerTransaction && (
                                        <div className="d-flex align-items-center" style={{ padding: "8px 0", gap: "8px" }}>
                                            <div className="flex-grow-1" style={{ fontSize: "14px", color: "#6c757d" }}>
                                                Unit Per Transaction
                                            </div>
                                            <span className="badge rounded-pill badge-soft-info fs-6 fw-medium">
                                                {data.UnitPerTransaction}
                                            </span>
                                        </div>
                                    )}
                                    {data?.AverageTransactionValue && (
                                        <div className="d-flex align-items-center" style={{ padding: "8px 0", gap: "8px" }}>
                                            <div className="flex-grow-1" style={{ fontSize: "14px", color: "#6c757d" }}>
                                                Average Transaction Value
                                            </div>
                                            <span className="badge rounded-pill badge-soft-info fs-6 fw-medium">
                                                {data.AverageTransactionValue}
                                            </span>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        )}

                        {/* ===== PIE / EMPTY ===== */}
                        <Card style={cardStyle}>
                            <CardBody style={{ padding: "14px 16px" }}>
                                <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "8px", marginBottom: "8px" }}>
                                    <span className="badge rounded-pill badge-soft-primary font-size-12 fw-medium" style={{ padding: "6px 12px" }}>
                                        First Bill: {data?.FirstBillTime ? convertTo12Hour(data.FirstBillTime) : "--"}
                                    </span>
                                    <span className="badge rounded-pill badge-soft-primary font-size-12 fw-medium" style={{ padding: "6px 12px" }}>
                                        Last Bill: {data?.LastBillTime ? convertTo12Hour(data.LastBillTime) : "--"}
                                    </span>
                                </div>

                                <div id="pie-chart" className="e-chart">
                                    {hasItems ? (
                                        <Pie
                                            Item={data.TopSaleItems}
                                            overallSalesAmount={data?.TotalAmount}
                                            key={`${shownRange.fromDate}_${shownRange.toDate}`}
                                        />
                                    ) : (
                                        renderEmptyState("230px")
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </>
                )}
            </div>
        </div>
    )
}

export default DailyItemSaleView